<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router';
import goToRoom from '@/utils/go-to-room';

const router = useRouter()
const props = defineProps({
  roomHandle: { required: true },
  disabled: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  bgImage: { type: String, default: null },
  avatar: { type: String, default: null },
  title: { type: String, default: '' },
  current: { type: Boolean, default: false },
  bgColor: { type: String, default: null },
  type: { type: String, default: 'default' } // navigation
})

const computedStyle = computed(() => ({
  '--bg-path': props.bgImage ? `url('${props.bgImage}')` : undefined,
  '--bg-color': props.bgColor
}))

const openRoom = () => {
  if (props.roomHandle === 'alessandra' || props.roomHandle === 'vegetario' || props.roomHandle === 'angie') {
    window.location.href = `/static/${props.roomHandle}`
  } else {
    goToRoom(router, props.roomHandle)
  }
}

</script>

<template>
  <div
    @click="openRoom"
    :style="computedStyle"
    :class="[
      'room-card',
      { 'room-card--current': current, disabled: disabled || !open },
      `room-card--${type}`
    ]"
  >
    <div class="room-card__wrapper">
      <div v-if="!open" class="coming-soon">coming soon</div>
      <div class="room-card__details">
        <img v-if="avatar" class="room-card__avatar" :src="avatar" :alt="title || 'Room avatar'" />
        <h3 v-if="title" class="room-card__title">{{ title }}</h3>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/sass_variables.scss' as *;

.disabled {
  pointer-events: none;
  // opacity: 0.8;
}
.room-card.disabled .room-card__title,
.room-card.disabled .room-card__avatar {
  filter: blur(4px);
}


.room-card {
  color: var(--clr-white);
  text-decoration: none;
  border-radius: 16px;
  overflow: clip;

  width: 100%;
  height: 100%;

  display: inline-flex;
  justify-content: center;

  @media screen and (min-width: $breakpoint-medium) {
    border-radius: 24px;
  }

  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    border-radius: 16px;

    width: 100%;
    height: 100%;

    background: var(--bg-path) no-repeat center;
    background-size: cover;
    overflow: clip;

    .coming-soon {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1.5rem;
      border-radius: 16px;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      z-index: 10;
    }

    @media screen and (min-width: $breakpoint-medium) {
      border-radius: 24px;
    }
  }

  &--default {
    aspect-ratio: 1;

    @media screen and (min-width: $breakpoint-medium) {
      aspect-ratio: 1.43;
      width: 100%;
    }
  }

  .room-card__title {
    font-weight: 600;
    font-size: 14px;

    @media screen and (min-width: $breakpoint-medium) {
      font-size: 20px;
    }

    @media screen and (min-width: 1400px) {
      font-size: 28px;
    }

  }

  &--highlight {
    .room-card__title {
      font-size: 16px;
    }

    .room-card__avatar {
      width: 44px;
    }

    .room-card__wrapper {
      height: 216px;
    }


    .room-card__details {
      padding: 76px 12px 8px;
      gap: 12px;
    }
  }

  &--navigation {
    width: 152px;
    height: 152px;
    padding: 4px;
    flex-shrink: 0;

    .room-card__wrapper {
      border-radius: 16px;
    }

    .room-card__avatar {
      width: 32px;
    }

    .room-card__title {
      font-size: 0.75rem;
    }

    .coming-soon {
      font-size: 1rem;
    }

    &.room-card--current > .room-card__wrapper {
      border: none;
      outline: 2px solid #55a3c4;
      outline-offset: 2px;
      box-shadow: none;
    }
  }

  &--fixed-size {
    .room-card__avatar {
      width: 32px;
    }

    .room-card__title {
      font-size: 18px;
    }

    .coming-soon {
      font-size: 18px;
    }
  }

  &--current {
    border-radius: 16px;
    &__wrapper {
      border: 2px solid #80D8FF;
      box-shadow: 0px 0px 16px 0px #80D8FF, 0px 0px 6px 0px #80D8FF, 0px 0px 1.125px 0px #80D8FF, 0px 0px 7.875px 0px #80D8FF;
    }
  }

  &__details {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 76px 8px 8px;
    background: linear-gradient(0deg, var(--bg-color) 3%, rgba(0, 0, 0, 0) 100%);
    border-radius: 0 0 16px 16px;
    z-index: 20;

    // This is a Safari hack to force the z-index to work
    -webkit-transform: translate3d(0,0,0);

    @media screen and (min-width: $breakpoint-medium) {
      .rooms & {
        padding: 0 20px 20px 20px;
        gap: 20px;
      }
    }
  }

  &__avatar {
    object-fit: cover;
    width: 32px;
    aspect-ratio: 1;
    border-radius: 50%;

    @media screen and (min-width: $breakpoint-medium) {
      width: 60px;
      .room-card__title {
        font-size: 0.75rem;
      }
    }

    @media screen and (min-width: 1400px) {
      width: 100px;
    }
  }
}
</style>
